import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'
import { withApiState } from '@/utils/withApiState'

export type FaqStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'
export type FaqActionKind = 'OPEN_PAGE' | 'TRIGGER_CAPABILITY' | 'EXTERNAL_LINK'

interface FaqTranslation {
  uuid: string
  language: string
  question: string
  answer: string
}

interface FaqAction {
  uuid: string
  order: number
  kind: FaqActionKind
  labels: Record<string, string>
  deniedMessages?: Record<string, string> | null
  targetPageKey?: string | null
  targetCapabilityKey?: string | null
  externalUrl?: string | null
  queryParams?: Record<string, unknown> | null
}

export interface FaqItemAdmin {
  uuid: string
  status: FaqStatus
  priority: number
  featureKeys: string[]
  pageKeys: string[]
  category: string | null
  tags: string[]
  availableLanguages: string[]
  questionFr: string | null
  actionsCount: number
  publishedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface FaqDetail extends FaqItemAdmin {
  translations: FaqTranslation[]
  actions: FaqAction[]
}

interface FaqTranslationInput {
  language: string
  question: string
  answer: string
}

export interface FaqActionInput {
  uuid?: string
  order: number
  kind: FaqActionKind
  labels: Record<string, string>
  deniedMessages?: Record<string, string>
  targetPageKey?: string | null
  targetCapabilityKey?: string | null
  externalUrl?: string | null
  queryParams?: Record<string, unknown>
}

export interface CreateFaqPayload {
  status?: FaqStatus
  priority?: number
  featureKeys?: string[]
  pageKeys?: string[]
  category?: string | null
  tags?: string[]
  translations: FaqTranslationInput[]
  actions?: FaqActionInput[]
}

export type UpdateFaqPayload = Omit<Partial<CreateFaqPayload>, 'translations'> & {
  translations?: FaqTranslationInput[]
}

export interface ListFaqQuery {
  status?: FaqStatus
  category?: string
  featureKey?: string
  pageKey?: string
  tag?: string
}

export interface PublishValidationError {
  code: string
  message: string
  detail?: unknown
}

export interface PublishResult {
  ok: boolean
  errors?: PublishValidationError[]
  faq?: FaqItemAdmin
}

type StoreResult<T> = { ok: true; data: T } | { ok: false; message: string }

async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json()
    if (body?.message) return Array.isArray(body.message) ? body.message.join(', ') : String(body.message)
  } catch {
    // ignore
  }
  return fallback
}

function buildQueryString(query: ListFaqQuery): string {
  const params = new URLSearchParams()
  if (query.status) params.append('status', query.status)
  if (query.category) params.append('category', query.category)
  if (query.featureKey) params.append('featureKey', query.featureKey)
  if (query.pageKey) params.append('pageKey', query.pageKey)
  if (query.tag) params.append('tag', query.tag)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export const useFaqAdminStore = defineStore('faqAdmin', () => {
  const suiteStore = useSuiteStore()
  const items = ref<FaqItemAdmin[]>([])
  const isLoading = ref(false)

  async function fetchAll(query: ListFaqQuery = {}): Promise<FaqItemAdmin[]> {
    const result = await withApiState(
      async () => {
        const res = await suiteStore.suiteFetch(`client-space/admin/faq${buildQueryString(query)}`, {
          method: 'GET',
          credentials: 'include',
        })
        if (res && typeof res !== 'boolean' && res.ok) {
          items.value = (await res.json()) as FaqItemAdmin[]
          return items.value
        }
        return undefined
      },
      { loading: isLoading, logLabel: 'Erreur récupération FAQ' },
    )
    return result ?? []
  }

  async function fetchDetail(uuid: string): Promise<FaqDetail | null> {
    const res = await suiteStore.suiteFetch(`client-space/admin/faq/${uuid}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      return (await res.json()) as FaqDetail
    }
    return null
  }

  async function create(payload: CreateFaqPayload): Promise<StoreResult<FaqDetail>> {
    const res = await suiteStore.suiteFetch('client-space/admin/faq', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as FaqDetail
      await fetchAll()
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, 'Erreur lors de la création')
      : 'Erreur lors de la création'
    return { ok: false, message }
  }

  async function update(uuid: string, payload: UpdateFaqPayload): Promise<StoreResult<FaqDetail>> {
    const res = await suiteStore.suiteFetch(`client-space/admin/faq/${uuid}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as FaqDetail
      await fetchAll()
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, 'Erreur lors de la mise à jour')
      : 'Erreur lors de la mise à jour'
    return { ok: false, message }
  }

  async function remove(uuid: string): Promise<StoreResult<void>> {
    const res = await suiteStore.suiteFetch(`client-space/admin/faq/${uuid}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      items.value = items.value.filter((i) => i.uuid !== uuid)
      return { ok: true, data: undefined }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, 'Erreur lors de la suppression')
      : 'Erreur lors de la suppression'
    return { ok: false, message }
  }

  async function publish(uuid: string): Promise<StoreResult<PublishResult>> {
    const res = await suiteStore.suiteFetch(`client-space/admin/faq/${uuid}/publish`, {
      method: 'POST',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as PublishResult
      if (data.ok) await fetchAll()
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, 'Erreur lors de la publication')
      : 'Erreur lors de la publication'
    return { ok: false, message }
  }

  async function archive(uuid: string): Promise<StoreResult<FaqItemAdmin>> {
    const res = await suiteStore.suiteFetch(`client-space/admin/faq/${uuid}/archive`, {
      method: 'POST',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as FaqItemAdmin
      await fetchAll()
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, "Erreur lors de l'archivage")
      : "Erreur lors de l'archivage"
    return { ok: false, message }
  }

  return {
    items,
    isLoading,
    fetchAll,
    fetchDetail,
    create,
    update,
    remove,
    publish,
    archive,
  }
})
